import { useMemo } from 'react';
import { useBrowserRoute } from './useBrowserRoute.ts';
import { parsePath, buildPath } from './parsePath.ts';

export type FleetView = 'dashboard' | 'map' | 'list' | 'detail' | 'teleop' | 'scan';

export const FLEET_VIEWS: readonly FleetView[] = [
  'dashboard',
  'map',
  'list',
  'detail',
  'teleop',
  'scan',
] as const;

export interface FleetRoute {
  view: FleetView;
  robotId: string | undefined;
  scanId: string | undefined;
  /* The robot page's open surface, as a path segment. A tab that is only
     component state cannot be linked, cannot be reloaded onto, and puts
     Back one page further out than the operator went — and the three
     workspace tabs on the same shell (`Operations` / `Robot list` / `Map`)
     are already routes, so the console had two tab idioms 100px apart. */
  detailTab: string | undefined;
  navigate: (
    view: FleetView,
    options?: { robotId?: string; scanId?: string; tab?: string },
  ) => void;
}

const VIEW_SET = new Set<string>(FLEET_VIEWS);

function isView(value: string): value is FleetView {
  return VIEW_SET.has(value);
}

// In-workspace route grammar:
//   /fleet                     → dashboard (default)
//   /fleet/list                → list
//   /fleet/map                 → map
//   /fleet/detail/rover-01     → detail page for rover-01 (Overview)
//   /fleet/detail/rover-01/config → the same page, Configuration open
//   /fleet/teleop/rover-01     → teleoperation console for rover-01 (PRD-0002)
//   /fleet/scan/42             → COPC viewer for scan id 42 (PRD-0006 D8)
export function useFleetRoute(): FleetRoute {
  const { path, setPath } = useBrowserRoute();

  return useMemo(() => {
    const { workspace, segments } = parsePath(path);

    let view: FleetView = 'dashboard';
    let robotId: string | undefined;
    let scanId: string | undefined;
    let detailTab: string | undefined;

    if (workspace === 'fleet') {
      const head = segments[0];
      if (head !== undefined && isView(head)) view = head;
      if (view === 'detail' || view === 'teleop') {
        const id = segments[1];
        if (id !== undefined && id.length > 0) robotId = id;
        const t = segments[2];
        if (view === 'detail' && t !== undefined && t.length > 0) detailTab = t;
      } else if (view === 'scan') {
        const id = segments[1];
        if (id !== undefined && id.length > 0) scanId = id;
      }
    }

    const navigate: FleetRoute['navigate'] = (nextView, options = {}) => {
      const parts: string[] = [nextView];
      if (nextView === 'detail' || nextView === 'teleop') {
        const id = options.robotId ?? (nextView === view ? robotId : undefined);
        if (id !== undefined && id.length > 0) parts.push(id);
        /* The default surface is the bare path, so one view never has two
           URLs and a link pasted into chat is the shortest true one. */
        if (nextView === 'detail' && options.tab !== undefined && options.tab !== 'overview') {
          parts.push(options.tab);
        }
      } else if (nextView === 'scan') {
        const id = options.scanId ?? (nextView === view ? scanId : undefined);
        if (id !== undefined && id.length > 0) parts.push(id);
      }
      setPath(buildPath('fleet', ...parts));
    };

    return { view, robotId, scanId, detailTab, navigate };
  }, [path, setPath]);
}
